import L from 'leaflet';
import { RotatingIcon } from './RotatingIcon';

/**
 * Event data for marker movement events
 */
interface MarkerMovementEvent {
  latlng: L.LatLng;
  rotation: number;
}

/**
 * Animation function type
 */
interface AnimationFunction {
  (t: number): number;
}

export class MovingMarker extends L.Marker {
  private animationId: number | null = null;
  private startTime: number | null = null;
  private startPos: L.LatLng | null = null;
  private targetPos: L.LatLng | null = null;
  private startRotation: number = 0;
  private targetRotation: number = 0;
  private duration: number = 1000;
  private readonly rotatingIcon: RotatingIcon | null = null;

  constructor(latlng: L.LatLng, options?: L.MarkerOptions) {
    super(latlng, options);
    this.startPos = latlng;

    if (options?.icon instanceof RotatingIcon) {
      this.rotatingIcon = options.icon;
    }
  }

  /**
   * Move the marker to a new position with animation
   * @param latlng The destination position
   * @param duration Animation duration in milliseconds
   * @param rotationAngle Optional rotation angle in degrees
   * @returns this for chaining
   */
  moveTo(
    latlng: L.LatLng,
    duration: number = 1000,
    rotationAngle?: number,
  ): this {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    this.startPos = this.getLatLng();
    this.targetPos = latlng;
    this.duration = duration;
    this.startTime = Date.now();

    this.startRotation = this.getRotationAngle();
    if (rotationAngle !== undefined) {
      this.targetRotation = rotationAngle;
    }

    this.fire('start', {
      latlng: this.startPos,
      rotation: this.startRotation,
    } as MarkerMovementEvent);

    this.animate();
    return this;
  }

  /**
   * Set the rotation angle of the marker
   * @param angle Rotation angle in degrees
   * @returns this for chaining
   */
  setRotationAngle(angle: number): this {
    if (this.rotatingIcon) {
      this.rotatingIcon.rotate(angle);
    }
    return this;
  }

  /**
   * Get the current rotation angle
   * @returns The rotation angle in degrees or 0 if not available
   */
  getRotationAngle(): number {
    return this.rotatingIcon ? this.rotatingIcon.getRotation() : 0;
  }

  private animate = () => {
    if (!this.startTime || !this.startPos || !this.targetPos) return;

    const elapsed = Date.now() - this.startTime;
    const progress = Math.min(elapsed / this.duration, 1);

    /**
     * Custom animation functions
     * Linear: `t`
     * Ease In-Out Quad: `t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t`
     * Fast In Slow Out: `t < 0.1 ? 2 * t * t : 0.1 + (1 - Math.pow(1 - 2 * (t - 0.1), 3)) / 2`
     * @param t
     */
    const easeInEaseOutQuadAnimation: AnimationFunction = (
      t: number,
    ): number => {
      return t;
    };

    const motionAtStartAnimation: AnimationFunction = (t: number): number => {
      const scaledT = Math.min(t / 0.2, 1);
      return 1 - Math.pow(1 - scaledT, 6);
    };

    const easedPositionProgress = easeInEaseOutQuadAnimation(progress);
    const easedRotationProgress = motionAtStartAnimation(progress);

    // Animate position
    const newLat =
      this.startPos.lat +
      (this.targetPos.lat - this.startPos.lat) * easedPositionProgress;
    const newLng =
      this.startPos.lng +
      (this.targetPos.lng - this.startPos.lng) * easedPositionProgress;

    this.setLatLng([newLat, newLng]);

    // Animate rotation
    if (this.rotatingIcon && this.startRotation !== this.targetRotation) {
      let startAngle = this.startRotation % 360;
      let endAngle = this.targetRotation % 360;
      if (startAngle < 0) startAngle += 360;
      if (endAngle < 0) endAngle += 360;
      let diff = endAngle - startAngle;
      if (Math.abs(diff) > 180) {
        diff = diff > 0 ? diff - 360 : diff + 360;
      }
      const currentAngle = startAngle + diff * easedRotationProgress;
      this.setRotationAngle(currentAngle);
    }

    if (progress < 1) {
      this.animationId = requestAnimationFrame(this.animate);
    } else {
      this.setLatLng(this.targetPos);
      if (this.rotatingIcon) {
        this.setRotationAngle(this.targetRotation);
      }
      this.fire('destination', {
        latlng: this.targetPos,
        rotation: this.targetRotation,
      } as MarkerMovementEvent);
      this.animationId = null;
    }
  };

  /**
   * Stop the current animation
   */
  stop(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Check if the marker is currently moving
   * @returns true if the marker is moving, false otherwise
   */
  isMoving(): boolean {
    return this.animationId !== null;
  }
}

// Add to L namespace
(L as any).MovingMarker = MovingMarker;
(L as any).movingMarker = (latlng: L.LatLng, options?: L.MarkerOptions) => {
  return new MovingMarker(latlng, options);
};
